/**
 * Página de Información Organizativa - Portal de Transparencia
 * Estilo Awwwards Interior - Estructura organizativa de INTERCASA, S.A.
 */

import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Información Organizativa con estilo Awwwards
 * @returns {JSX.Element} Página de información organizativa
 */
const OrganizationalInfoPage = () => {
  const heroRef = useRef(null);
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Portal Transparencia */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <Link 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">Portal de Transparencia</span>
            </Link>
            
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6 drop-shadow-lg">
              Información de<br />
              <span className="text-intercasa-yellow">Administradores y Directivos</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              Administradores y alta dirección de INTERCASA, S.A.
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">

            {/* Información de Administradores y Directivos */}
            <AnimatedSection animation="fadeUp" delay={0.1} className="mb-16">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                Información General Alta Dirección
              </span>
              <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                Administradores Mancomunados
              </h2>
            
              <div className="space-y-6">
                {/* Guillermo Serrano Pérez */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-intercasa-dark rounded-xl flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                      GS
                    </div>
                    <div>
                      <h3 className="font-baskerville text-xl font-bold text-intercasa-dark">
                        D. Guillermo Serrano Pérez
                      </h3>
                      <p className="text-sm text-intercasa-red font-medium">
                        Administrador Mancomunado de INTERCASA, S.A.
                      </p>
                      <p className="text-xs text-intercasa-gray mt-1">
                        Representante persona física de Grupo Martinón Grumasa, S.L.
                      </p>
                    </div>
                  </div>
                
                  <div className="space-y-4 text-intercasa-dark/70 leading-relaxed">
                    <p>
                      Ingeniero Industrial por la Universidad de Las Palmas de Gran Canaria (2009), con calificación media de Notable.
                    </p>
                    <p>
                      Posee además el Título de Técnico en Prevención de Riesgos Laborales, Nivel Superior, especialidad en Seguridad Laboral (Bilbao, 2009), y el diploma en Gestión de la Innovación para la mejora de la competitividad empresarial por la Universidad Politécnica de Madrid (2012).
                    </p>
                    <p>
                      A lo largo de su trayectoria profesional, ha desarrollado una sólida carrera en el ámbito de la ingeniería, la innovación y la gestión empresarial.
                    </p>
                    <p>
                      Su perfil combina la formación técnica con la dirección estratégica de organizaciones. Ha participado activamente en el rediseño de modelos de negocio, la mejora de procesos, el desarrollo de productos y la ejecución de proyectos de I+D+i.
                    </p>
                    <div>
                      <p className="text-intercasa-dark font-medium mb-3">Entre los principales hitos de su carrera destacan:</p>
                      <ul className="space-y-3 ml-4">
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><strong>Consultor en Edei Consultores (2009-2012):</strong> participación en más de quince proyectos de distinta índole económica, abarcando áreas de gestión de la innovación, emprendimiento, planes de negocio, implantación de herramientas TIC y gestión de organizaciones públicas y privadas.</span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><strong>Gerente en INTERCASA, S.A. (2012-actualidad):</strong> dirección y coordinación de los Departamentos de Administración, Financiero, Recursos Humanos, Compras, Producción, Mantenimiento, Calidad, I+D+i, Comercial y Marketing.</span>
                        </li>
                      </ul>
                    </div>
                    <p>
                      Su gestión se ha caracterizado por un enfoque integral hacia la eficiencia, la innovación tecnológica y la sostenibilidad industrial.
                    </p>
                  </div>
                </div>
              
                {/* Yonay José Alemán García */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-intercasa-dark rounded-xl flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                      YA
                    </div>
                    <div>
                      <h3 className="font-baskerville text-xl font-bold text-intercasa-dark">
                        D. Yonay José Alemán García
                      </h3>
                      <p className="text-sm text-intercasa-red font-medium">
                        Administrador Mancomunado de INTERCASA, S.A.
                      </p>
                    </div>
                  </div>
                
                  <div className="space-y-4 text-intercasa-dark/70 leading-relaxed">
                    <p>
                      Licenciado en Administración y Dirección de Empresas por la Universidad de Las Palmas de Gran Canaria (2009-2012) y Experto en Asesoría Fiscal por la misma universidad (2019-2020).
                    </p>
                    <p>
                      Desde el año 2015 desarrolla su actividad profesional en el Grupo Martinón, desempeñando distintos cargos vinculados a la gestión financiera y directiva.
                    </p>
                    <div>
                      <p className="text-intercasa-dark font-medium mb-3">Su experiencia incluye:</p>
                      <ul className="space-y-3 ml-4">
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><strong>Controller Financiero (2015-2021):</strong> responsable del control y análisis financiero en diversos centros de negocio del Grupo Martinón, especializado en la gestión de tesorería, revisión del cumplimiento de objetivos financieros y análisis de la rentabilidad de las actividades.</span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><strong>Director Financiero en la rama industrial del Grupo (desde 2021):</strong> incorporación a INTERCASA, S.A. con responsabilidad sobre la gestión financiera, control presupuestario y planificación económica de la entidad.</span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><strong>Director General en la división hotelera del Grupo (2023-2025):</strong> dirección orientada a la optimización de la explotación hotelera, mejora de la rentabilidad y fortalecimiento de la gestión operativa.</span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><strong>Director General de INTERCASA, S.A. (desde 2025):</strong> lidera el proceso de modernización integral de la compañía, impulsando la transformación tecnológica, la optimización de los procesos productivos, la sostenibilidad industrial y la apertura de nuevos canales de venta y oportunidades de negocio.</span>
                        </li>
                      </ul>
                    </div>
                    <p>
                      Su trayectoria refleja un compromiso constante con la excelencia operativa, la innovación en la gestión y la consolidación del crecimiento sostenible de la empresa.
                    </p>
                  </div>
                </div>

                {/* Retribución */}
                <div className="bg-intercasa-dark rounded-2xl p-6 md:p-8 text-white">
                  <h4 className="font-baskerville text-lg font-bold mb-3 text-intercasa-yellow">
                    Retribución Alta Dirección
                  </h4>
                  <p className="text-white/70">
                    Los cargos de administradores mancomunados no están remunerados (artículo 26 de los Estatutos de INTERCASA, S.A.) por lo que la retribución como administradores es la que perciben como trabajadores según su categoría profesional.
                  </p>
                </div>
              </div>

              {/* Botones de descarga */}
              <div className="flex flex-wrap gap-4 justify-center mt-6">
                <a 
                  href="/PortalTansparenciaIntercasa/InformacionAdmin/InformacionAdministradoresyDirectivos.pdf" 
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar PDF
                </a>
                <a
                  href="/PortalTansparenciaIntercasa/InformacionAdmin/InformacionAdministradoresyDirectivos.odt"
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar ODT
                </a>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default OrganizationalInfoPage;
